import { useMemo, useState } from 'react';
import { useData } from '../lib/data';
import { useSelection } from '../lib/selection';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import type { Task, TaskStatus } from '../lib/database.types';
import { DueChip } from '../components/DueChip';
import { AddTaskForm } from '../components/AddTaskForm';
import { EditableName } from '../components/EditableName';

const ARCHIVABLE_STATES = ['building', 'deployed', 'paused', 'optimizing', 'paper-trading', 'live'];

const ACTIVE = new Set<TaskStatus>(['pending', 'in_progress', 'blocked']);

export function ProjectView({ slug }: { slug: string }) {
  const { projects, clients, tasksByProject, childrenByParent, upsertProjectLocal } = useData();
  const { select } = useSelection();
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const project = projects.find((p) => p.slug === slug);
  const client = project?.client_slug
    ? clients.find((c) => c.slug === project.client_slug)
    : undefined;

  const topTasks = useMemo(
    () => (tasksByProject.get(slug) ?? []).filter((t) => t.parent_task_id === null),
    [tasksByProject, slug],
  );

  const activeTasks = useMemo(
    () =>
      [...topTasks]
        .filter((t) => ACTIVE.has(t.status as TaskStatus))
        .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [topTasks],
  );

  const completedTasks = useMemo(
    () => topTasks.filter((t) => t.status === 'completed').slice(0, 10),
    [topTasks],
  );

  if (!project) {
    return (
      <div className="flex-1 grid place-items-center text-dim font-mono text-sm">
        build not found: {slug}
      </div>
    );
  }

  const archiveProject = async (nextState: 'archived' | 'paused') => {
    if (READONLY) return;
    setArchiveBusy(true);
    await supabase.from('tb_projects').update({ state: nextState }).eq('slug', project.slug);
    setArchiveBusy(false);
    setShowArchiveConfirm(false);
    select({ type: 'home' });
  };

  const canArchive = ARCHIVABLE_STATES.includes(project.state);
  const isArchived = project.state === 'archived' || project.state === 'completed';

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-8">
        <header className="space-y-2">
          <nav className="text-xs font-mono text-dim flex items-center gap-2">
            <button onClick={() => select({ type: 'home' })} className="hover:text-ink">
              home
            </button>
            {client && (
              <>
                <span>›</span>
                <button
                  onClick={() => select({ type: 'client', slug: client.slug })}
                  className="hover:text-ink"
                >
                  {client.display_name}
                </button>
              </>
            )}
            <span>›</span>
            <span className="text-muted">build</span>
          </nav>

          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-semibold tracking-tight flex-1">
              <EditableName
                value={project.name}
                onSave={async (next) => {
                  const { error } = await supabase
                    .from('tb_projects')
                    .update({ name: next })
                    .eq('slug', project.slug);
                  if (error) throw new Error(error.message);
                  upsertProjectLocal({ ...project, name: next });
                }}
                inputClassName="text-3xl font-semibold tracking-tight"
              />
            </h1>
            {canArchive && !showArchiveConfirm && (
              <button
                onClick={() => setShowArchiveConfirm(true)}
                className="btn text-xs shrink-0"
                title="archive this build"
              >
                archive
              </button>
            )}
            {isArchived && (
              <button
                onClick={() => archiveProject('paused')}
                disabled={archiveBusy}
                className="btn text-xs shrink-0"
                title="unarchive (set back to paused)"
              >
                unarchive
              </button>
            )}
          </div>

          {showArchiveConfirm && (
            <div className="panel p-3 flex items-center justify-between gap-3">
              <div className="text-xs font-mono text-muted">
                archive this build? It will be hidden from the sidebar until
                you click "show archived".
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setShowArchiveConfirm(false)}
                  className="btn text-xs"
                  disabled={archiveBusy}
                >
                  cancel
                </button>
                <button
                  onClick={() => archiveProject('archived')}
                  className="btn-primary text-xs"
                  disabled={archiveBusy}
                >
                  {archiveBusy ? 'archiving…' : 'archive'}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap text-sm">
            {project.engagement_stage && (
              <span className="chip chip-low">{project.engagement_stage}</span>
            )}
            <span className="chip chip-low">{project.state}</span>
          </div>

          {project.description && (
            <p className="text-sm text-muted mt-2">{project.description}</p>
          )}
        </header>

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">Active tasks</h2>
            <span className="text-xs text-dim font-mono">{activeTasks.length}</span>
          </div>
          <div className="space-y-1.5">
            {activeTasks.map((t) => (
              <TaskRow
                key={t.id}
                task={t}
                children={childrenByParent.get(t.id) ?? []}
                onOpen={() => select({ type: 'task', id: t.id })}
              />
            ))}
            {activeTasks.length === 0 && (
              <div className="panel p-8 text-center text-dim font-mono text-sm">
                no active tasks yet
              </div>
            )}
          </div>
          <AddTaskForm defaultProjectSlug={slug} />
        </section>

        {completedTasks.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-mono tracking-widest text-dim uppercase">
              recently done
            </h2>
            <div className="space-y-1">
              {completedTasks.map((t) => (
                <div
                  key={t.id}
                  onClick={() => select({ type: 'task', id: t.id })}
                  className="px-3 py-2 text-sm text-dim line-through cursor-pointer
                             hover:text-muted transition-colors"
                >
                  {t.title}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function TaskRow({
  task,
  children,
  onOpen,
}: {
  task: Task;
  children: Task[];
  onOpen: () => void;
}) {
  const doneKids = children.filter((c) => c.status === 'completed').length;

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (READONLY) return;
    const next = task.status === 'completed' ? 'pending' : 'completed';
    await supabase
      .from('tb_tasks')
      .update({
        status: next,
        completed_at: next === 'completed' ? new Date().toISOString() : null,
      })
      .eq('id', task.id);
  };

  return (
    <div
      onClick={onOpen}
      className="panel p-4 flex items-center gap-3 hover:border-borderHi
                 hover:bg-panelHi/40 transition-colors cursor-pointer"
    >
      <button aria-label="toggle complete" onClick={toggle} className="check-hit">
        <span
          className={
            'check-dot ' +
            (task.status === 'completed' ? 'bg-green border-green' : 'border-border hover:border-amber')
          }
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-2">
          {task.title}
          {children.length > 0 && (
            <span className="chip chip-low">
              {doneKids}/{children.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted font-mono mt-1 flex-wrap">
          <span className={`chip chip-${task.priority}`}>{task.priority}</span>
          <DueChip dueAt={task.due_at} />
          {task.status === 'blocked' && <span className="chip chip-critical">blocked</span>}
          {task.status === 'in_progress' && (
            <span className="chip chip-high">in progress</span>
          )}
        </div>
      </div>
    </div>
  );
}
